import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Form, FormRenderProps } from 'react-final-form';
import { Box, Button } from '@mui/material';

import GenericDialog from '../GenericDialog';
import ContentBox from '../layout/ContentBox';
import { Institute, AppActionTypes, RegistrationField } from '../../redux/types';
import registerFormConfig from '../register/registerFormConfig';
import ReactFinalFormWrapper from '../register/ReactFinalFormWrapper';
import { setInitialInstituteData, parseInsituteData } from '../utilities/InstituteUtility';

type Props = {
  isOpen: boolean;
  institute: Institute;
  locale: string;
  onClose: (refreshFlag?: boolean) => void;
  showNotification?: (message: string | React.ReactElement) => AppActionTypes
  updateInstitute?: (institute: Institute) => void;
  disableInstituteCountry?: boolean;
  lockCountry?: boolean
  showForwardAddress?: boolean
}

const InstituteEditDialog: React.FC<Props> = ({
  isOpen,
  institute: propsInstitute,
  locale,
  onClose,
  showNotification,
  updateInstitute,
  disableInstituteCountry = false,
  lockCountry = false,
  showForwardAddress = false,
}: Props) => {
  const handleSubmitRef = useRef<any>();
  const isError = useRef<boolean>();

  const onDoSave = async (values: Record<string, any>): Promise<void> => {
    const { institute } = values;
    const instituteDataToSave = parseInsituteData(institute);
    if (updateInstitute) {
      const updatedInstitute = { ...propsInstitute, ...instituteDataToSave };
      if (!instituteDataToSave.invoiceName) {
        updatedInstitute.invoiceCity = null;
        updatedInstitute.invoiceCountry = null;
        updatedInstitute.invoiceHousenumber = null;
        updatedInstitute.invoiceName = null;
        updatedInstitute.invoiceStreet = null;
        updatedInstitute.invoiceZip = null;
      }
      if (!instituteDataToSave.certificationName) {
        updatedInstitute.certificationCity = null;
        updatedInstitute.certificationCountry = null;
        updatedInstitute.certificationHousenumber = null;
        updatedInstitute.certificationName = null;
        updatedInstitute.certificationHospitalName = null;
        updatedInstitute.certificationStreet = null;
        updatedInstitute.certificationZip = null;
      }
      const { forwardAddresses } = updatedInstitute;
      const trimmedForwardAddresses = forwardAddresses
        && forwardAddresses.length > 0
        && forwardAddresses.join('').trim();
      if (!trimmedForwardAddresses) {
        updatedInstitute.forwardAddresses = null;
      }
      await updateInstitute(updatedInstitute);
    }

    if (showNotification) {
      showNotification(<FormattedMessage id="genericSaveMessage" />);
    }

    if (onClose) {
      onClose(true);
    }
  };

  const instituteFormConfiguration = registerFormConfig(locale, !lockCountry)[1];
  const { fields } = instituteFormConfiguration;

  // to add property disable to country list so that
  // user would not be able to switch country
  // Why? Institute country control access to which trial they can register
  const modifiedFields = (fields as RegistrationField[]).map((field) => {
    if (field.key === 'institute.instituteCountry') {
      return { ...field, disable: disableInstituteCountry };
    }
    if (field.key.indexOf('institute.forward') > -1 && showForwardAddress) {
      return { ...field, hide: false };
    }
    return field;
  });

  const newInstituteFormConfig = { ...instituteFormConfiguration, fields: modifiedFields };
  return (
    <GenericDialog isOpen={isOpen}>
      <ContentBox title={<FormattedMessage id="instituteEditDialog.edit" />}>
        <Form
          onSubmit={onDoSave}
          initialValues={setInitialInstituteData(propsInstitute)}
          render={({ form, handleSubmit }: FormRenderProps) => {
            if (!handleSubmitRef.current) {
              handleSubmitRef.current = handleSubmit;
            }

            isError.current = Object.keys(form.getState().errors || {}).length > 0;

            return (
              <div>
                <form className="flex flex-wrap">
                  <ReactFinalFormWrapper registrationForm={newInstituteFormConfig} />
                </form>
              </div>
            );
          }}
        />
        <Box display="flex" gap={1} justifyContent="right">
          <Button
            variant="contained"
            disabled={isError.current}
            color="primary"
            onClick={() => handleSubmitRef.current()}
          >
            <FormattedMessage id="save" />
          </Button>
          <Button
            variant="contained"
            onClick={() => onClose()}
            color="secondary"
          >
            <FormattedMessage id="genericClose" />
          </Button>
        </Box>
      </ContentBox>
    </GenericDialog>
  );
};

export default InstituteEditDialog;
